import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";

const RemovePlayerModal = ({
  isOpen,
  onClose,
  onConfirm,
  playerName = "",
  loading = false,
}) => {
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (isOpen) {
      setReason("");
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event) => {
      if (event.key === "Escape" && !loading) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, loading, onClose]);

  const handleConfirm = () => {
    if (!reason.trim()) {
      return;
    }
    onConfirm(reason.trim());
  };

  const handleCancel = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) {
    return null;
  }

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/50"
        onClick={handleCancel}
      />
      <div className="relative flex min-h-full items-end justify-center p-4 md:items-center">
        <div className="w-full max-w-lg rounded-t-2xl bg-white p-6 shadow-xl md:rounded-2xl">
          {/* Header */}
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-[#1A212F]">
                Remove from Squad
              </h2>
              <p className="mt-1 text-sm text-[#657084]">
                Are you sure you want to remove{" "}
                <span className="font-semibold">{playerName}</span> from your
                squad?
              </p>
            </div>
            <button
              type="button"
              className="text-gray-400 hover:text-gray-600"
              onClick={handleCancel}
              disabled={loading}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M15 5L5 15M5 5L15 15"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          {/* Reason Textarea */}
          <div className="mb-6">
            <label
              htmlFor="removal-reason"
              className="mb-2 block text-sm font-medium text-[#1A212F]"
            >
              Reason for removal <span className="text-red-500">*</span>
            </label>
            <textarea
              id="removal-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g., Transferred, Wyscout not updated yet"
              className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2 text-sm text-[#1A212F] placeholder-gray-400 focus:border-[#2283FF] focus:outline-none focus:ring-1 focus:ring-[#2283FF] resize-none"
              rows={4}
              disabled={loading}
              style={{ resize: "none" }}
              maxLength={500}
            />
            <div className="mt-1 flex items-center justify-between">
              <p className="text-xs text-[#657084]">
                Please provide a brief explanation
              </p>
              <p className="text-xs text-[#657084]">
                {reason.length}/500
              </p>
            </div>
          </div>

          {/* Helper Text */}
          <div className="mb-6 rounded-lg bg-red-50 p-3">
            <p className="text-sm text-red-800">
              This action will remove the player from your squad. This
              cannot be undone.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex flex-col gap-3 md:flex-row md:justify-end">
            <button
              type="button"
              onClick={handleCancel}
              className={`w-full rounded-lg border border-[#E6E6E6] px-4 py-2 text-sm font-medium text-[#1E1E1E] transition-colors hover:bg-gray-50 md:w-auto ${
                loading ? "cursor-not-allowed opacity-60" : ""
              }`}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!reason.trim() || loading}
              className={`w-full rounded-lg bg-[#FF3B30] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-600 md:w-auto ${
                !reason.trim() || loading
                  ? "cursor-not-allowed opacity-60 hover:bg-[#FF3B30]"
                  : ""
              }`}
            >
              {loading ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  <span>Processing...</span>
                </div>
              ) : (
                "Remove Player"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default RemovePlayerModal;
